import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { AppImage } from "@/src/components/NextImage";

import { ShellLink } from "@/src/components";
import log from "logbeacon";
import { useMemo } from "react";
import { cn } from "utils";
import { PivotAlertItem } from "./PivotAlertItem";
import { ExchangeEnum } from "@/src/features/api/openData";
import { I18nUtil } from "@utils/i18n";
import { ROUTER_PATH } from "@/src/config";
import Translations from "@translate/components/Home/PivotAlert";
import { isKoreanUser } from "@/src/utils/locale";
import { useAgentMarketPairsWithDetail } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

export const PivotAlert = ({ className }: { className?: string }) => {
  const exchange = useMemo(() => {
    const isKorean = isKoreanUser();
    return isKorean ? ExchangeEnum.UPBIT : ExchangeEnum.BINANCE;
  }, []);
  const { data: pairsDetail } = useAgentMarketPairsWithDetail({
    params: {
      exchange,
      "enrich.with_fractal_point.enable": true,
    },
    type: "short",
  });
  const pairsDetailData = useMemo(() => {
    log.debug("[HomeKlineModule] pairsDetail", pairsDetail);
    if (Array.isArray(pairsDetail) && pairsDetail.length > 0) {
      // 取前两个
      return pairsDetail.slice(0, 2).map((i) => {
        const lastPoint = i.fractal_point?.at(-1);

        const hasTicks =
          lastPoint?.type === "BEARISH_FRACTAL" ||
          lastPoint?.type === "BULLISH_FRACTAL";
        const startIndex = Number(i?.ohlc?.length) - 10;
        const x = lastPoint?.index != null ? lastPoint.index - startIndex : 0;
        const clampedX = Math.max(0, Math.min(9, x));

        return {
          ...i,
          chatData: i?.ohlc
            ?.map((ii: any) => ii.close)
            .slice(i?.ohlc?.length - 10, i?.ohlc?.length),
          ticks: hasTicks
            ? [
                {
                  x: clampedX,
                  color:
                    lastPoint?.type === "BULLISH_FRACTAL"
                      ? ospThemes.colors.border_accent1_solid
                      : ospThemes.colors.border_accent2_solid,
                },
              ]
            : [],
        };
      });
    }

    return [];
  }, [pairsDetail]);

  if (!pairsDetailData.length) {
    return null;
  }

  return (
    <div
      className={cn(
        "p-4 bg-background_white_overlay5 rounded-3 md:mt-6 mt-0",
        className,
      )}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center justify-center">
          <AppImage
            src="/assets/images/pivot-alert.webp"
            alt="Pivot Alert"
            width={16}
            height={16}
          />
          <span className="title_l text-content_primary ml-2">
            {I18n.t("Pivot Alert")}
          </span>
        </div>
        <ShellLink
          aria-label="Pivot Alert"
          data-openreplay-label={`Pivot Alert Link | ${ROUTER_PATH.PIVOT_ALERT}`}
          id="lnk-pivot-alert"
          className="gap-0.5 botton_s text-content_tertiary flex items-center justify-center"
          href={ROUTER_PATH.PIVOT_ALERT}
        >
          <span className="button_s">{I18n.t("View all")}</span>
          <Icon
            name="arrow-right"
            size={16}
            className="text-content_tertiary"
          />
        </ShellLink>
      </div>
      <div className="relative mt-3 flex flex-col gap-3">
        {pairsDetailData.map((i) => (
          <PivotAlertItem
            data={{
              logo: i.logo || "",
              base: i.base || "",
              price: `${i?.price || ""}` || "",
              chatData: i.chatData || [],
              ticks: i.ticks,
            }}
            isKorean={isKoreanUser()}
            key={i.base}
          />
        ))}
      </div>
    </div>
  );
};
